import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, BookOpen, Brain, Headphones, ClipboardCheck, ArrowRight,
  Crown, Check,
} from "lucide-react";
import { createChild, completeWizard, startCheckout, listChildren } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BoardPicker from "@/components/BoardPicker";

const STEPS = ["welcome", "child", "plan"];

function Welcome({ user, next }) {
  const features = [
    { icon: BookOpen, label: "Lessons", color: "bg-[#D4C4FB]" },
    { icon: Brain, label: "Flashcards", color: "bg-[#FFE066]" },
    { icon: Headphones, label: "Podcasts", color: "bg-[#9DFFB0]" },
    { icon: ClipboardCheck, label: "Quizzes", color: "bg-[#FFB38E]" },
  ];
  return (
    <div className="text-center">
      <div className="inline-flex items-center gap-2 nb-border rounded-full px-4 py-1.5 bg-[#FFE066] mb-6">
        <Sparkles className="w-4 h-4" strokeWidth={3} />
        <span className="label-caps">AQA · Edexcel · OCR, built for parents</span>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-3">
        Welcome,{" "}
        <span className="bg-[#9DFFB0] dark:bg-[#68D391] px-3 nb-border rounded-2xl inline-block text-black">
          {user?.name?.split(" ")[0] || "friend"}
        </span>
      </h1>
      <p className="text-zinc-700 dark:text-zinc-300 font-medium mb-8 max-w-md mx-auto">
        Let&apos;s set up your child&apos;s revision in 2 quick steps. After that they&apos;ll have AI-powered lessons, flashcards, podcasts and quizzes, matched to their exam board.
      </p>
      <div className="grid grid-cols-4 gap-3 sm:gap-4 max-w-md mx-auto mb-8">
        {features.map((f, i) => (
          <motion.div
            key={f.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            className={`nb-border rounded-xl ${f.color} p-3 sm:p-4 text-black`}
          >
            <f.icon className="w-5 h-5 mx-auto mb-1" strokeWidth={2.5} />
            <div className="font-display font-extrabold text-[11px] sm:text-xs">{f.label}</div>
          </motion.div>
        ))}
      </div>
      <button
        data-testid="welcome-next"
        onClick={next}
        className="nb-button px-8 py-4 bg-[#D4C4FB] dark:bg-[#B794F4] flex items-center gap-2 mx-auto"
      >
        Let&apos;s set up <ArrowRight className="w-4 h-4" strokeWidth={3} />
      </button>
    </div>
  );
}

function AddChild({ onCreated, max }) {
  const [name, setName] = useState("");
  const [year, setYear] = useState(10);
  const [board, setBoard] = useState("AQA GCSE");
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) { toast.error("PIN must be 4 digits"); return; }
    setSaving(true);
    try {
      const c = await createChild({
        name: name.trim(),
        year_group: year,
        pin,
        avatar_seed: name.trim(),
        exam_board: board,
      });
      onCreated(c);
    } catch (err) {
      // If the parent already has a child (e.g. multi-tab signup), recover
      // gracefully instead of dead-ending on a 402.
      if (err?.response?.status === 402) {
        try {
          const kids = await listChildren();
          if (kids && kids.length > 0) {
            toast.success("Welcome back, your profile is ready");
            onCreated(kids[0]);
            return;
          }
        } catch (_) { /* fall through to error toast */ }
      }
      toast.error(err?.response?.data?.detail || "Failed to add child");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit}>
      <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-2">
        Add your{" "}
        <span className="bg-[#FFB38E] dark:bg-[#F6AD55] px-3 nb-border rounded-2xl inline-block text-black">
          child
        </span>
      </h2>
      <p className="text-zinc-600 dark:text-zinc-400 font-medium mb-6 text-sm">
        Each child gets their own profile, progress and streak. {max > 1 ? `Your plan allows ${max} profiles.` : "Add more on the Family plan."}
      </p>

      <label className="label-caps block mb-2">Child&apos;s name</label>
      <input
        data-testid="wizard-name"
        required autoFocus value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Lily"
        className="nb-input mb-4"
      />

      <label className="label-caps block mb-2">Year group</label>
      <select
        data-testid="wizard-year"
        value={year} onChange={(e) => setYear(parseInt(e.target.value))}
        className="nb-input mb-4"
      >
        {[7,8,9,10,11,12,13].map((y) => <option key={y} value={y}>Year {y}</option>)}
      </select>

      <label className="label-caps block mb-2">Exam board</label>
      <div className="mb-4">
        <BoardPicker value={board} onChange={setBoard} testIdPrefix="wizard-board" />
      </div>

      <label className="label-caps block mb-2">4-digit PIN (so they can log in)</label>
      <input
        data-testid="wizard-pin"
        required inputMode="numeric" maxLength={4} pattern="\d{4}"
        value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
        placeholder="e.g. 1234"
        className="nb-input mb-6 text-center text-2xl tracking-[0.5em]"
      />

      <button
        type="submit"
        disabled={saving || !name.trim() || pin.length !== 4}
        data-testid="wizard-add-child"
        className="nb-button px-6 py-3 w-full bg-[#9DFFB0] dark:bg-[#68D391] flex items-center justify-center gap-2"
      >
        {saving ? "Saving…" : "Next →"}
      </button>
    </form>
  );
}

function PickPlan({ onFinish, child }) {
  const [loading, setLoading] = useState(null);
  const navigate = useNavigate();

  const subscribe = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      // Mark wizard done first so they don't loop back here regardless of payment outcome.
      await completeWizard();
      const { checkout_url } = await startCheckout(lookup_key);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(null);
    }
  };

  const startFree = async () => {
    setLoading("free");
    try {
      await completeWizard();
      toast.success(`${child?.name || "Your child"} is all set!`);
      navigate("/curriculum");
    } catch {
      setLoading(null);
    }
  };

  return (
    <div>
      <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-2">
        Pick a{" "}
        <span className="bg-[#D4C4FB] dark:bg-[#B794F4] px-3 nb-border rounded-2xl inline-block text-black">
          plan
        </span>
      </h2>
      <p className="text-zinc-600 dark:text-zinc-400 font-medium mb-6 text-sm">
        Start free or try Premium, every plan comes with a 7-day free trial. Cancel anytime.
      </p>

      <div className="nb-card p-5 mb-3 bg-[#D4C4FB] dark:bg-[#B794F4] text-black">
        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-4 h-4" strokeWidth={2.5} />
          <span className="label-caps">Recommended</span>
        </div>
        <div className="flex items-baseline gap-2 mb-2">
          <div className="font-display text-3xl font-black">£4.99</div>
          <div className="font-bold text-sm">/month after 7 days free</div>
        </div>
        <ul className="space-y-1 text-sm font-medium mb-4">
          <li className="flex gap-1.5"><Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={3}/> Full GCSE curriculum (AQA / Edexcel / OCR)</li>
          <li className="flex gap-1.5"><Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={3}/> Unlimited AI podcasts</li>
          <li className="flex gap-1.5"><Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={3}/> Adaptive quizzes + parent dashboard</li>
        </ul>
        <button
          onClick={() => subscribe("premium_monthly")}
          disabled={loading === "premium_monthly"}
          data-testid="wizard-subscribe-premium"
          className="nb-button px-5 py-3 w-full bg-black text-white dark:bg-white dark:text-black"
        >
          {loading === "premium_monthly" ? "Loading…" : "Start 7-day free trial"}
        </button>
      </div>

      <button
        onClick={startFree}
        disabled={loading === "free"}
        data-testid="wizard-start-free"
        className="nb-button px-5 py-3 w-full text-sm"
      >
        {loading === "free" ? "Loading…" : "I’ll try the free 3 topics first"}
      </button>
    </div>
  );
}

export default function WelcomeWizard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [child, setChild] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  // If the parent already has a child (e.g. they completed AddChild in a prior
  // session but bailed before PickPlan), skip step 2, re-submitting would 402
  // because they'd exceed max_children.
  useEffect(() => {
    let cancelled = false;
    listChildren()
      .then((kids) => {
        if (cancelled) return;
        if (kids && kids.length > 0) {
          setChild(kids[0]);
          setStep(2); // jump to PickPlan
        }
      })
      .catch(() => { /* anonymous/401, leave at step 0 */ })
      .finally(() => { if (!cancelled) setBootstrapping(false); });
    return () => { cancelled = true; };
  }, []);

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));

  if (bootstrapping) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950 flex items-center justify-center p-4">
        <div className="font-bold text-zinc-500">Setting things up…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950 flex items-center justify-center p-4 sm:p-6">
      <div className="max-w-lg w-full">
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-6">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full nb-border transition-all ${
                i === step ? "w-10 bg-[#D4C4FB] dark:bg-[#B794F4]" : "w-5 bg-white dark:bg-zinc-900"
              }`}
            />
          ))}
        </div>

        <div className="nb-card p-6 sm:p-8 nb-shadow-xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 14 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -14 }}
              transition={{ duration: 0.2 }}
            >
              {step === 0 && <Welcome user={user} next={next} />}
              {step === 1 && (
                <AddChild
                  max={user?.max_children || 1}
                  onCreated={async (c) => { setChild(c); await refresh(); next(); }}
                />
              )}
              {step === 2 && <PickPlan child={child} onFinish={() => navigate("/curriculum")} />}
            </motion.div>
          </AnimatePresence>
        </div>

        {step > 0 && step < STEPS.length - 1 && (
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            data-testid="wizard-back"
            className="block mx-auto mt-4 text-sm font-bold text-zinc-500 hover:underline"
          >
            ← Back
          </button>
        )}
      </div>
    </div>
  );
}
